import { numberWithCommas } from 'src/utils/number';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from 'src/components/ui/hover-card';

interface BreakdownItem {
  symbol: string;
  percentage: number;
  total_value_usd: number;
  price_usd: number | null;
}

interface TokenAssetAllocationBreakdownProps {
  breakdown: BreakdownItem[];
}

// Color palette for tokens - using a professional, accessible color scheme
const TOKEN_COLORS = [
  '#3B82F6', // Blue - for major tokens like USDC
  '#8B5CF6', // Purple - for ETH
  '#EC4899', // Pink - for other major tokens
  '#10B981', // Green
  '#F59E0B', // Orange
  '#EF4444', // Red
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#F97316', // Orange variant
  '#84CC16', // Lime
];

const OTHERS_COLOR = '#9CA3AF'; // Gray for Others

export const TokenAssetAllocationBreakdown = ({
  breakdown,
}: TokenAssetAllocationBreakdownProps) => {
  return (
    <div className="space-y-4">
      {/* Horizontal bar chart */}
      <div className="flex h-4 w-full overflow-hidden rounded-full bg-gray-100">
        {breakdown.map((item, index) => (
          <HoverCard key={item.symbol} openDelay={150} closeDelay={100}>
            <HoverCardTrigger asChild>
              <div
                className="cursor-pointer transition-all duration-200 hover:opacity-80"
                style={{
                  width: `${item.percentage}%`,
                  backgroundColor: getItemColor(index, item.symbol),
                }}
              />
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              {getHoverCardContent(item, index)}
            </HoverCardContent>
          </HoverCard>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {breakdown.map((item, index) => (
          <HoverCard key={item.symbol} openDelay={150} closeDelay={100}>
            <HoverCardTrigger asChild>
              <div className="flex cursor-pointer items-center gap-2 transition-opacity hover:opacity-80">
                <div
                  className="h-3 w-3 rounded-sm"
                  style={{
                    backgroundColor: getItemColor(index, item.symbol),
                  }}
                />
                <span className="text-xs text-muted-foreground">
                  {item.symbol} {item.percentage.toFixed(2)}%
                </span>
              </div>
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              {getHoverCardContent(item, index)}
            </HoverCardContent>
          </HoverCard>
        ))}
      </div>
    </div>
  );
};

const getHoverCardContent = (item: BreakdownItem, index: number) => {
  if (item.symbol === 'Others') {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div
            className="h-4 w-4 rounded-sm"
            style={{ backgroundColor: OTHERS_COLOR }}
          />
          <h4 className="font-semibold text-foreground">{item.symbol}</h4>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Allocation:</span>
            <span className="font-medium">{item.percentage.toFixed(2)}%</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Value:</span>
            <span className="font-medium">
              ${numberWithCommas(item.total_value_usd, 2)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div
          className="h-4 w-4 rounded-sm"
          style={{ backgroundColor: TOKEN_COLORS[index % TOKEN_COLORS.length] }}
        />
        <h4 className="font-semibold text-foreground">{item.symbol}</h4>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Allocation:</span>
          <span className="font-medium">{item.percentage.toFixed(2)}%</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Total Value:</span>
          <span className="font-medium">
            ${numberWithCommas(item.total_value_usd, 2)}
          </span>
        </div>
        {item.price_usd != null && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Price:</span>
            <span className="font-medium">
              ${numberWithCommas(item.price_usd, 4)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const getItemColor = (index: number, symbol: string) => {
  return symbol === 'Others'
    ? OTHERS_COLOR
    : TOKEN_COLORS[index % TOKEN_COLORS.length];
};
